import { Icons } from "assets";
import classNames from "classnames";
import { useEffect, useState } from "react";
import CustomBadge from "shared/components/common/customBadge";
import CustomPagination from "shared/components/common/customPagination";
import CustomSearch from "shared/components/common/customSearch";
import CustomTable from "shared/components/common/customTable";
import CustomToolTip from "shared/components/common/customToolTip";
import { toastMessage } from "shared/components/common/toast";
import SkeletonLoader from "shared/loaders/skeleton";
import ConfirmationModal from "shared/modal/confirmation";
import ViewCustomerModal from "shared/modal/viewCustomerModal";
import { ActivateUserService, BlockUserService } from "shared/services/users";
import styles from "./style.module.scss";
import useDebounce from "shared/hooks/useDebounce";
import { GetAllCustomersService } from "shared/services/customers";

interface CustomerData {
  user_id: number;
  type: number;
  user: {
    id: number;
    fullname: string;
    phone_number: string;
    address: string;
    postal_code: string;
    email_notifications: string;
    city: string;
    type: number;
    status: number;
    auth: {
      email: string;
    };
  };
}

const DashboardCustomers = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [viewModal, setViewModal] = useState<boolean>(false);
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    action: string | null;
    customer: CustomerData | null;
  }>({
    open: false,
    action: null,
    customer: null,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [customers, setCustomers] = useState<CustomerData[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerData | null>(
    null
  );
  const [actionType, setActionType] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");

  const truncateText = (text: string, length: number) => {
    return text.length > length ? `${text.substring(0, length)}...` : text;
  };

  const openViewModal = (customer: CustomerData) => {
    setSelectedCustomer(customer);
    setViewModal(true);
    setActionType(customer.user.status === 1 ? "Block" : "Activate");
  };

  const closeViewModal = () => {
    setViewModal(false);
    setSelectedCustomer(null);
    setActionType("");
  };

  const openConfirmModalFromViewModal = () => {
    if (!selectedCustomer) return;
    setConfirmModal({
      open: true,
      action: actionType,
      customer: selectedCustomer,
    });
  };

  const openConfirmModal = (action: string, customer: CustomerData) => {
    setConfirmModal({ open: true, action, customer });
  };

  const closeConfirmModal = () => {
    setConfirmModal({ open: false, action: null, customer: null });
  };

  const handleConfirmAction = async (
    action: string,
    customer: CustomerData
  ) => {
    const {
      user: { id },
    } = customer;

    setActionLoading(true);
    try {
      const response =
        action === "Activate"
          ? await ActivateUserService(id)
          : await BlockUserService(id);
      if (response?.status === 200) {
        toastMessage(
          "success",
          `Customer ${action.toLowerCase()}d successfully!`
        );
        setCustomers((prevCustomers) =>
          prevCustomers.map((cust) =>
            cust.user?.id === id
              ? {
                  ...cust,
                  user: { ...cust.user, status: action === "Activate" ? 1 : 0 },
                }
              : cust
          )
        );
      }
    } catch (error) {
      toastMessage("error", `Error: Could not ${action.toLowerCase()} user`);
    } finally {
      setActionLoading(false);
      closeConfirmModal();
      closeViewModal();
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handleGetAllCustomers(page);
  };

  const supplierTableHeads = [
    { title: "Customer Name", key: "customer" },
    { title: "Phone Number", key: "phone_number" },
    { title: "Address", key: "address" },
    { title: "City", key: "city" },
    { title: "Status", key: "status" },
    { title: "Customer Type", key: "type" },
    { title: "Action", key: "action" },
  ];

  const handleGetAllCustomers = async (page = 1) => {
    try {
      setLoading(true);
      const response = await GetAllCustomersService(page, 10, searchValue);
      setCustomers(response.data.data.data || []);
      setTotalItems(response.data.data.meta.totalItems || 0);
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    handleGetAllCustomers();
    // eslint-disable-next-line
  }, [searchValue]);

  useDebounce(
    () => {
      setSearchValue(search);
    },
    [search],
    800
  );

  return (
    <div className={styles.sectionContainer}>
      <div className={styles.tableSearchHeader}>
        <div className="flex justify-end">
          <CustomSearch
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
            }}
          />
        </div>
      </div>

      <div className={classNames(styles.tableWrapper, "relative")}>
        <CustomTable
          heads={supplierTableHeads}
          title="Customer List"
          isEmpty={!loading && !customers.length}
        >
          {loading
            ? Array.from({ length: 2 }).map((_, index) => (
                <TableRowLoader key={index} />
              ))
            : customers.map((customer) => {
                const statusText =
                  customer.user.status === 1 ? "Active" : "Blocked";
                const typeText =
                  customer.type === 0 ? "Home Owner" : "Organization";
                return (
                  <tr key={customer.user?.id}>
                    <td>{truncateText(customer.user.fullname, 15)}</td>
                    <td>{customer.user.phone_number}</td>
                    <td>{customer.user.address}</td>
                    <td>{customer.user.city}</td>
                    <td>
                      <CustomBadge
                        textColor={
                          statusText === "Active" ? "#027A48" : "#ED1C24"
                        }
                        bg={statusText === "Active" ? "#ecfdf3" : "#ed1c240f"}
                        title={statusText}
                      />
                    </td>
                    <td>{typeText}</td>
                    <td className={classNames(styles.IconsContainer)}>
                      {customer.user.status === 1 ? (
                        <CustomToolTip label="Block">
                          <button
                            onClick={() => openConfirmModal("Block", customer)}
                          >
                            <Icons.Block />
                          </button>
                        </CustomToolTip>
                      ) : (
                        <CustomToolTip label="Activate">
                          <button
                            onClick={() =>
                              openConfirmModal("Activate", customer)
                            }
                          >
                            <Icons.CheckFilledLight />
                          </button>
                        </CustomToolTip>
                      )}
                      <CustomToolTip label="Details">
                        <button onClick={() => openViewModal(customer)}>
                          <Icons.Eye />
                        </button>
                      </CustomToolTip>
                    </td>
                  </tr>
                );
              })}
        </CustomTable>
      </div>

      <CustomPagination
        onPageChange={handlePageChange}
        totalCount={totalItems}
        currentPage={currentPage}
        pageSize={10}
      />

      {selectedCustomer && (
        <ViewCustomerModal
          title="Customer Details"
          show={viewModal}
          handleClose={closeViewModal}
          listingDetails={{
            name: selectedCustomer.user.fullname,
            phone: selectedCustomer.user.phone_number,
            address: selectedCustomer.user.address,
            postal_code: selectedCustomer.user.postal_code,
            city: selectedCustomer.user.city,
            email_notifications: selectedCustomer.user.email_notifications,
            profileImage: "",
            customerType: selectedCustomer.type,
            email: selectedCustomer?.user?.auth?.email,
          }}
          isHaveCustomerType
          actionButtonText={actionType}
          handleSubmit={openConfirmModalFromViewModal}
        />
      )}

      {confirmModal.open && (
        <ConfirmationModal
          show={confirmModal.open}
          title={`Confirm ${confirmModal.action}`}
          loading={actionLoading}
          actionButtonText={`Yes, ${confirmModal.action}`}
          subtitle={`Are you sure you want to ${confirmModal.action?.toLowerCase()} this customer?`}
          handleSubmit={() =>
            confirmModal.customer &&
            handleConfirmAction(confirmModal.action!, confirmModal.customer)
          }
          handleClose={closeConfirmModal}
        />
      )}
    </div>
  );
};

const TableRowLoader = () => (
  <tr>
    {Array.from({ length: 6 }).map((_, index) => (
      <td
        key={index}
        className={classNames(styles.tableContentLabel, "px-2 py-3")}
      >
        <div className="flex items-center justify-center">
          <SkeletonLoader className={styles.lineLoader} />
        </div>
      </td>
    ))}
  </tr>
);

export default DashboardCustomers;
